import { Header, ModalDialog, PrimaryButton, Space, Text } from '@procurify/ui'

import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { useProcurifyIntl } from '@webapp/hooks'
import { CreatePOCustomFieldFormPresenter } from '@webapp/modules/purchasing/features/create-po-custom-field/presenters/CreatePOCustomFieldFormPresenter'
import { POCustomFieldConfigListPresenter } from '@webapp/modules/purchasing/features/list-po-custom-field-configs/presenters/POCustomFieldConfigListPresenter'
import { EditablePOCustomFieldTextFieldPresenter } from '@webapp/modules/purchasing/features/rename-po-custom-field/presenters/EditablePOCustomFieldTextFieldPresenter'
import messages from './messages'
import { usePOCustomFieldsView } from './usePOCustomFieldsView'

/**
 * Feature Set: PO Custom Fields section (Custom Fields tab)
 *
 * Orchestrates:
 * - Heading/description row + "New custom field" button (FS composition)
 * - The list feature, with per-row rename cells injected via renderNameCell
 *   and row deletes delegated to the hook-only delete command (ADR-9)
 * - The FS-owned create ModalDialog wrapping the create form presenter
 */
export const POCustomFieldsView = () => {
  const intl = useProcurifyIntl()

  const {
    data: { fields, totalCount },
    states: { isCreateModalOpen },
    methods: { openCreateModal, closeCreateModal, onDeleteField },
  } = usePOCustomFieldsView()

  const renderNameCell = (field: CustomField) => (
    <EditablePOCustomFieldTextFieldPresenter field={field} allFields={fields} />
  )

  return (
    <>
      <Space>
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space size='small'>
            <Header as='h3'>{intl.formatMessage(messages.heading)}</Header>
            <Text>{intl.formatMessage(messages.description)}</Text>
          </Space>
          <PrimaryButton icon='add' onClick={openCreateModal}>
            {intl.formatMessage(messages.newCustomFieldButton)}
          </PrimaryButton>
        </Space>

        <POCustomFieldConfigListPresenter
          onDeleteField={onDeleteField}
          renderNameCell={renderNameCell}
        />
      </Space>

      {/* Create modal shell — owned by the Feature Set per ADR-9 */}
      <ModalDialog
        title={intl.formatMessage(messages.createModalTitle)}
        maxWidth='md'
        open={isCreateModalOpen}
        onClose={closeCreateModal}
      >
        {isCreateModalOpen && (
          <CreatePOCustomFieldFormPresenter
            existingFields={fields}
            totalCount={totalCount}
            onSuccess={closeCreateModal}
            onCancel={closeCreateModal}
          />
        )}
      </ModalDialog>
    </>
  )
}

POCustomFieldsView.displayName = 'POCustomFieldsView'
